/* Org-scoped resource functions. All calls go through api() from ./client. */
import { api, apiDownload, apiUpload, HttpError } from './client';
import type {
  ActivityPage,
  AlertPage,
  AnalyticsSummary,
  AwardDetail,
  AwardDeliverable,
  AwardHistoryResult,
  AwardPage,
  AwardStatus,
  AwardSummary,
  BidDecision,
  BillingInfo,
  CalendarEvent,
  CultivationStage,
  CustomFieldDef,
  Decision,
  DeliverableKind,
  DeliverableStatus,
  DocumentRecord,
  DuplicateContact,
  EmailStatus,
  EmailSyncResult,
  EventType,
  FeedStatus,
  GatesResult,
  FitScore,
  GoNoGoAssessment,
  ImportPreview,
  ImportResult,
  InteractionType,
  Member,
  MergeResult,
  OutreachReminder,
  ReminderStatus,
  StakeholderInteraction,
  MorningBrief,
  Opportunity,
  OpportunityAlert,
  OpportunityDetail,
  ProposalAttachment,
  ProposalAttachmentCategory,
  ProposalComment,
  ProposalComplianceItem,
  ProposalDetail,
  ProposalSection,
  ProposalStatus,
  ProposalSummary,
  ProposalVersionInfo,
  QuillDraftPreview,
  OrgProfile,
  OrgSettings,
  Page,
  PipelineStage,
  Plan,
  PortfolioResponse,
  ProfileCompletion,
  ProfileVersionInfo,
  Priority,
  RadarCheckResult,
  ReadinessItem,
  ReadinessOverview,
  ReadinessStatus,
  WebsiteImportResult,
  RegistrationView,
  SamRefreshResult,
  SamStatusView,
  SamSyncResult,
  SbaCertification,
  Stakeholder,
  StakeholderType,
  TaskItem,
  TaskStatus,
  TaxExemptView,
  TaxRefreshResult,
  NonprofitSearchItem,
  NonprofitOrganization,
  NonprofitDataSource,
  NonprofitSourceInfo,
  OnboardingPath,
  OnboardingProfile,
  OnboardingStage,
  OnboardingUpsertBody,
  TaxExemptStatus,
  SamStatus,
  PersonalizedPlan,
  VaultCompleteness,
  VaultDocument,
  VaultList,
  VaultReviewCheck,
  AiStatus,
  AiDraftSectionResult,
  ConciergeTurn,
  ConciergeHistoryItem,
  DocumentAnalysis,
  VaultUploadResult,
  WatchStatus,
  WizardDef,
  WorkbackReview,
  WizardRunResult,
  AutoVerification,
} from './types';

export interface ListQuery {
  page?: number;
  limit?: number;
  q?: string;
}

// ---------------- Opportunities ----------------

export interface OpportunityQuery extends ListQuery {
  stage?: string;
  type?: string;
  source?: string;
  naics?: string;
  setAside?: string;
  minScore?: number;
  sort?: 'deadline' | 'value' | 'score' | 'updated';
  order?: 'asc' | 'desc';
}

/** The viewer's IANA zone — the calendar frame the page renders in.
 *  Wave 3 (R2-BR-D17): sent with opportunity detail / go-nogo reads so
 *  server-computed day counts use the SAME frame as the display. */
function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export const opportunities = {
  list: (query?: OpportunityQuery) =>
    api<Page<Opportunity>>('/org/opportunities', { query }),
  create: (body: Partial<Opportunity>) =>
    api<Opportunity>('/org/opportunities', { method: 'POST', body }),
  get: (id: string) =>
    api<OpportunityDetail>(`/org/opportunities/${id}`, { query: { tz: viewerTimeZone() } }),
  update: (id: string, body: Partial<Opportunity>) =>
    api<Opportunity>(`/org/opportunities/${id}`, { method: 'PATCH', body }),
  archiveOverdue: () =>
    api<{ archived: number }>('/org/opportunities/archive-overdue', { method: 'POST' }),
  restore: (id: string) =>
    api<Opportunity>(`/org/opportunities/${id}/restore`, { method: 'POST' }),
  remove: (id: string) =>
    api<{ ok: true }>(`/org/opportunities/${id}`, { method: 'DELETE' }),
  recomputeScore: (id: string) =>
    api<FitScore>(`/org/opportunities/${id}/score`, { method: 'POST' }),
  goNoGo: (id: string, refresh?: boolean) =>
    api<GoNoGoAssessment>(`/org/opportunities/${id}/go-nogo`, {
      query: { tz: viewerTimeZone(), ...(refresh ? { refresh: '1' } : {}) },
    }),
  samSync: (body: {
    query?: string;
    naics?: string;
    limit?: number;
    postedFrom?: string;
  }) =>
    api<SamSyncResult>('/org/opportunities/sam-sync', {
      method: 'POST',
      body,
    }),
  generateGates: (id: string) =>
    api<GatesResult>(`/org/opportunities/${id}/gates`, { method: 'POST' }),
  /** Materialize the Forge workback milestones as real tasks (idempotent). */
  fromWorkback: (id: string) =>
    api<{ created: number; skipped: number; outdated: number; anchorDate?: string; anchorSource?: string }>(
      `/org/opportunities/${id}/tasks/from-workback`,
      { method: 'POST', body: {} },
    ),
  /** R2-BR-D65: move stale, incomplete workback tasks onto the current deadline's plan. */
  rescheduleWorkback: (id: string) =>
    api<{ updated: number; anchorDate: string; anchorSource: string; workbackReview: WorkbackReview }>(
      `/org/opportunities/${id}/tasks/reschedule-workback`,
      { method: 'POST', body: {} },
    ),
  awardHistory: (id: string) =>
    api<AwardHistoryResult>(`/org/opportunities/${id}/award-history`),
  grantsDetail: (id: string) =>
    api<{ opportunityId: string; detail: Record<string, unknown> }>(`/org/opportunities/${id}/grants-detail`),
};

// ---------------- Federal feeds ----------------

export const feed = {
  status: () => api<FeedStatus>('/org/feed/status'),
  sync: (body?: { sources?: string[] }) =>
    api<{ started: boolean; sources: string[] }>('/org/feed/sync', {
      method: 'POST',
      body: body ?? {},
    }),
};

// ---------------- Bid decisions ----------------

export const decisions = {
  list: (opportunityId: string, query?: ListQuery) =>
    api<Page<BidDecision>>(`/org/opportunities/${opportunityId}/decisions`, {
      query,
    }),
  create: (opportunityId: string, body: { decision: Decision; rationale: string }) =>
    api<BidDecision>(`/org/opportunities/${opportunityId}/decisions`, {
      method: 'POST',
      body,
    }),
};

// ---------------- Pipeline ----------------

export const pipeline = {
  get: (query?: { q?: string; source?: string; agency?: string; naics?: string }) =>
    api<{ stages: PipelineStage[] }>('/org/pipeline', { query }),
};

// ---------------- Portfolio (multi-workspace visibility) ----------------

export const portfolio = {
  get: () => api<PortfolioResponse>('/portfolio'),
};

// ---------------- Tasks ----------------

export interface TaskQuery extends ListQuery {
  status?: TaskStatus | '';
  priority?: Priority | '';
  ownerId?: string;
  opportunityId?: string;
}

export const tasks = {
  list: (query?: TaskQuery) => api<Page<TaskItem>>('/org/tasks', { query }),
  create: (body: Partial<TaskItem>) =>
    api<TaskItem>('/org/tasks', { method: 'POST', body }),
  update: (id: string, body: Partial<TaskItem>) =>
    api<TaskItem>(`/org/tasks/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ ok: true }>(`/org/tasks/${id}`, { method: 'DELETE' }),
};

// ---------------- Calendar ----------------

export interface CalendarQuery {
  from?: string;
  to?: string;
  eventType?: EventType | '';
  opportunityId?: string;
}

export const calendar = {
  list: (query?: CalendarQuery) => api<Page<CalendarEvent>>('/org/calendar', { query }),
  create: (body: Partial<CalendarEvent>) =>
    api<CalendarEvent>('/org/calendar', { method: 'POST', body }),
  update: (id: string, body: Partial<CalendarEvent>) =>
    api<CalendarEvent>(`/org/calendar/${id}`, { method: 'PATCH', body }),
  remove: (id: string) =>
    api<{ ok: true }>(`/org/calendar/${id}`, { method: 'DELETE' }),
};

// ---------------- Stakeholders ----------------

export const stakeholders = {
  list: (query?: ListQuery & { stakeholderType?: StakeholderType | ''; cultivationStage?: CultivationStage | '' }) =>
    api<Page<Stakeholder>>('/org/stakeholders', { query }),
  create: (body: Partial<Stakeholder>) =>
    api<Stakeholder>('/org/stakeholders', { method: 'POST', body }),
  update: (id: string, body: Partial<Stakeholder>) =>
    api<Stakeholder>(`/org/stakeholders/${id}`, { method: 'PATCH', body }),
  remove: (id: string) =>
    api<{ ok: true }>(`/org/stakeholders/${id}`, { method: 'DELETE' }),
  fieldDefs: {
    get: () => api<{ defs: CustomFieldDef[] }>('/org/stakeholders/field-defs'),
    save: (defs: CustomFieldDef[]) =>
      api<{ defs: CustomFieldDef[] }>('/org/stakeholders/field-defs', { method: 'PUT', body: { defs } }),
  },
  duplicates: () => api<{ groups: DuplicateContact[][] }>('/org/stakeholders/duplicates'),
  merge: (winnerId: string, loserIds: string[]) =>
    api<MergeResult>('/org/stakeholders/merge', { method: 'POST', body: { winnerId, loserIds } }),
  importPreview: (csv: string, mapping?: Record<string, string>) =>
    api<ImportPreview>('/org/stakeholders/import/preview', { method: 'POST', body: { csv, mapping } }),
  importCommit: (rows: Record<string, string>[], onDuplicate: 'skip' | 'update') =>
    api<ImportResult>('/org/stakeholders/import/commit', { method: 'POST', body: { rows, onDuplicate } }),
};

// ---------------- Relationship manager ----------------

export const relationships = {
  interactions: {
    list: (stakeholderId: string) =>
      api<{ items: StakeholderInteraction[] }>(`/org/relationships/stakeholders/${stakeholderId}/interactions`),
    create: (stakeholderId: string, body: { type?: InteractionType; occurredAt?: string; summary: string; outcome?: string; opportunityId?: string }) =>
      api<StakeholderInteraction>(`/org/relationships/stakeholders/${stakeholderId}/interactions`, { method: 'POST', body }),
    remove: (stakeholderId: string, interactionId: string) =>
      api<{ ok: true }>(`/org/relationships/stakeholders/${stakeholderId}/interactions/${interactionId}`, { method: 'DELETE' }),
  },
  reminders: {
    list: (query?: ListQuery & { status?: ReminderStatus | ''; stakeholderId?: string; dueBefore?: string }) =>
      api<Page<OutreachReminder>>('/org/relationships/reminders', { query }),
    due: () =>
      api<{ items: OutreachReminder[]; now: string }>('/org/relationships/reminders/due'),
    create: (body: { title: string; notes?: string; dueAt: string; stakeholderId?: string; opportunityId?: string }) =>
      api<OutreachReminder>('/org/relationships/reminders', { method: 'POST', body }),
    update: (id: string, body: { title?: string; notes?: string | null; dueAt?: string; status?: ReminderStatus; snoozedUntil?: string | null }) =>
      api<OutreachReminder>(`/org/relationships/reminders/${id}`, { method: 'PATCH', body }),
    remove: (id: string) =>
      api<{ ok: true }>(`/org/relationships/reminders/${id}`, { method: 'DELETE' }),
  },
};

// ---------------- Email capture ----------------

export const emailCapture = {
  status: () => api<EmailStatus>('/org/email/status'),
  connectUrl: () => api<{ url: string }>('/org/email/google/url'),
  removeAccount: (id: string) => api<{ ok: true }>(`/org/email/accounts/${id}`, { method: 'DELETE' }),
  sync: () => api<EmailSyncResult>('/org/email/sync', { method: 'POST', body: {} }),
};

// ---------------- Documents ----------------

export const documents = {
  list: (query?: ListQuery & { category?: string; opportunityId?: string }) =>
    api<Page<DocumentRecord>>('/org/documents', { query }),
  upload: (formData: FormData) =>
    apiUpload<DocumentRecord>('/org/documents', formData),
  download: (id: string) => apiDownload(`/org/documents/${id}/download`),
  remove: (id: string) =>
    api<{ ok: true }>(`/org/documents/${id}`, { method: 'DELETE' }),
};

// ---------------- Analytics ----------------

export const analytics = {
  get: () => api<AnalyticsSummary>('/org/analytics'),
  /** R2-G2 (g): pipeline report CSV — the reporting export the plan copy promises. */
  exportCsv: () => apiDownload('/org/analytics/export.csv'),
};

// ---------------- Billing ----------------

export const billing = {
  get: () => api<BillingInfo>('/org/billing'),
  checkout: (plan: Plan) =>
    api<{ url: string }>('/org/billing/checkout', {
      method: 'POST',
      body: { plan },
    }),
  /** Batch 2 Item 1: a Stripe Billing Portal session for this workspace's own customer. */
  portal: () => api<{ url: string }>('/org/billing/portal', { method: 'POST', body: {} }),
};

// ---------------- Settings & members ----------------

export const settings = {
  get: () => api<OrgSettings>('/org/settings'),
  rename: (name: string) =>
    api<{ id: string; name: string; plan: Plan }>('/org/settings', {
      method: 'PATCH',
      body: { name },
    }),
};

export const members = {
  list: () => api<{ items: Member[] }>('/org/members'),
  add: (email: string, role: 'ADMIN' | 'MEMBER') =>
    api<Member>('/org/members', { method: 'POST', body: { email, role } }),
  remove: (userId: string) =>
    api<{ ok: true }>(`/org/members/${userId}`, { method: 'DELETE' }),
};

// ---------------- Profile (Organization DNA) ----------------

export const profile = {
  get: () => api<OrgProfile>('/org/profile'),
  update: (data: Record<string, unknown>) =>
    api<OrgProfile>('/org/profile', { method: 'PUT', body: { data } }),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return apiUpload<{ logoUrl: string }>('/org/profile/logo', form);
  },
  removeLogo: () => api<{ logoUrl: null }>('/org/profile/logo', { method: 'DELETE' }),
  logoUrl: () => '/api/v1/org/profile/logo',
  versions: (query?: ListQuery) =>
    api<Page<ProfileVersionInfo>>('/org/profile/versions', { query }),
  completion: () => api<ProfileCompletion>('/org/profile/completion'),
  importWebsite: (website?: string) =>
    api<WebsiteImportResult>('/org/profile/import-website', {
      method: 'POST',
      body: website ? { website } : {},
    }),
};

// ---------------- Audit ----------------

export interface AuditItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  details: unknown;
  user: { id: string; name: string } | null;
  createdAt: string;
}

export const audit = {
  list: (query?: { entityType?: string; limit?: number }) =>
    api<Page<AuditItem>>('/org/audit', { query }),
};

// ---------------- Proposals (authoring workspace) ----------------

export const proposals = {
  list: () => api<{ items: ProposalSummary[] }>('/org/proposals'),
  create: (body: { opportunityId: string; title?: string; ownerId?: string; deadline?: string }) =>
    api<ProposalSummary>('/org/proposals', { method: 'POST', body }),
  get: (id: string) => api<ProposalDetail>(`/org/proposals/${id}`),
  update: (id: string, body: Partial<{ title: string; status: ProposalStatus; ownerId: string | null; deadline: string | null }>) =>
    api<{ id: string; status: ProposalStatus }>(`/org/proposals/${id}`, { method: 'PATCH', body }),
  addSection: (id: string, body: { title: string; body?: string; wordTarget?: number }) =>
    api<ProposalSection>(`/org/proposals/${id}/sections`, { method: 'POST', body }),
  updateSection: (id: string, sectionId: string, body: Partial<ProposalSection>) =>
    api<ProposalSection>(`/org/proposals/${id}/sections/${sectionId}`, { method: 'PATCH', body }),
  removeSection: (id: string, sectionId: string) =>
    api<{ ok: true }>(`/org/proposals/${id}/sections/${sectionId}`, { method: 'DELETE' }),
  addComment: (id: string, body: { body: string; sectionId?: string }) =>
    api<ProposalComment>(`/org/proposals/${id}/comments`, { method: 'POST', body }),
  updateComment: (id: string, commentId: string, body: Partial<{ body: string; resolved: boolean }>) =>
    api<ProposalComment>(`/org/proposals/${id}/comments/${commentId}`, { method: 'PATCH', body }),
  removeComment: (id: string, commentId: string) =>
    api<{ ok: true }>(`/org/proposals/${id}/comments/${commentId}`, { method: 'DELETE' }),
  addCompliance: (id: string, body: { label: string; detail?: string; linkedSectionId?: string }) =>
    api<ProposalComplianceItem>(`/org/proposals/${id}/compliance`, { method: 'POST', body }),
  updateCompliance: (id: string, itemId: string, body: Partial<ProposalComplianceItem>) =>
    api<ProposalComplianceItem>(`/org/proposals/${id}/compliance/${itemId}`, { method: 'PATCH', body }),
  removeCompliance: (id: string, itemId: string) =>
    api<{ ok: true }>(`/org/proposals/${id}/compliance/${itemId}`, { method: 'DELETE' }),
  uploadAttachment: (id: string, file: File, category: ProposalAttachmentCategory) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    return apiUpload<ProposalAttachment>(`/org/proposals/${id}/attachments`, fd);
  },
  downloadAttachment: (id: string, attachmentId: string) =>
    apiDownload(`/org/proposals/${id}/attachments/${attachmentId}/download`),
  removeAttachment: (id: string, attachmentId: string) =>
    api<{ ok: true }>(`/org/proposals/${id}/attachments/${attachmentId}`, { method: 'DELETE' }),
  saveVersion: (id: string, label: string) =>
    api<ProposalVersionInfo>(`/org/proposals/${id}/versions`, { method: 'POST', body: { label } }),
  restoreVersion: (id: string, versionId: string) =>
    api<{
      ok: true;
      restoredVersionId: string;
      restoredLabel: string;
      safetySnapshotId: string;
      safetySnapshotLabel: string;
      sectionCount: number;
    }>(`/org/proposals/${id}/versions/${versionId}/restore`, { method: 'POST' }),
  quillDraft: (id: string, signal?: AbortSignal) =>
    api<{
      sections: ProposalSection[];
      snapshotId: string;
      snapshotLabel?: string;
    }>(`/org/proposals/${id}/quill-draft`, { method: 'POST', signal }),
  quillDraftPreview: (id: string, signal?: AbortSignal) =>
    api<QuillDraftPreview>(`/org/proposals/${id}/quill-draft/preview`, { method: 'POST', signal }),
  recordSubmission: (
    id: string,
    body: { submittedAt: string; expectedResponseDate?: string | null; submissionReference?: string | null },
  ) => api<ProposalSummary>(`/org/proposals/${id}/record-submission`, { method: 'POST', body }),
  /** Batch 2 Item 3: the printable submission record (labelled in the document itself). */
  submissionReceiptPdf: (id: string) => apiDownload(`/org/proposals/${id}/submission-receipt.pdf`),
  /** Batch 2 Item 2: apply the guided interview's user-approved saves to the org profile. */
  interviewSave: (
    id: string,
    body: {
      saves: {
        question: string;
        answer: string;
        field: 'dnaVoice' | 'dnaPrograms' | 'dnaImpact' | 'dnaTrackRecord' | 'dnaBoilerplate';
        sectionTitle?: string;
        mode?: 'append' | 'replace';
      }[];
    },
  ) =>
    api<{ saved: number; skipped: number; version: number; fields: { field: string; mode: string }[] }>(
      `/org/proposals/${id}/interview-save`,
      { method: 'POST', body },
    ),
  addBlockerDisposition: (id: string, body: { blockerKey: string; note: string }) =>
    api<{ id: string; blockerKey: string; note: string; byName: string | null }>(
      `/org/proposals/${id}/blocker-dispositions`,
      { method: 'POST', body },
    ),
  removeBlockerDisposition: (id: string, dispositionId: string) =>
    api<{ ok: true }>(`/org/proposals/${id}/blocker-dispositions/${dispositionId}`, { method: 'DELETE' }),
  exportPdf: (id: string) => apiDownload(`/org/proposals/${id}/export.pdf`),
  exportDocx: (id: string) => apiDownload(`/org/proposals/${id}/export.docx`),
};

// ---------------- Document analysis (RFP/grant solicitation uploads) ----------------

export type AnalysisLifecycle = 'active' | 'saved' | 'archived';

export const analyses = {
  list: (lifecycle: AnalysisLifecycle | 'all' = 'active') =>
    api<{ items: DocumentAnalysis[]; counts: Record<AnalysisLifecycle, number>; total: number }>(
      `/org/analyses?lifecycle=${lifecycle}`,
    ),
  get: (id: string) => api<{ analysis: DocumentAnalysis }>(`/org/analyses/${id}`),
  upload: (files: File | File[], portalUrl?: string) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    // Single file keeps the legacy "file" field; groups use "files".
    for (const f of list) fd.append(list.length === 1 ? 'file' : 'files', f);
    if (portalUrl?.trim()) fd.append('portalUrl', portalUrl.trim());
    return apiUpload<{ analysis: DocumentAnalysis }>('/org/analyses', fd);
  },
  reanalyze: (id: string) =>
    api<{ analysis: DocumentAnalysis }>(`/org/analyses/${id}/reanalyze`, { method: 'POST' }),
  addToPipeline: (id: string) =>
    api<{ opportunity: { id: string; title: string }; pipelineItem: { id: string } }>(
      `/org/analyses/${id}/pipeline`,
      { method: 'POST' },
    ),
  setLifecycle: (id: string, lifecycle: AnalysisLifecycle) =>
    api<{ analysis: DocumentAnalysis }>(`/org/analyses/${id}`, {
      method: 'PATCH',
      body: { lifecycle },
    }),
  remove: (id: string) =>
    api<{ deleted: boolean; id: string; vaultDocumentsDeleted: number }>(`/org/analyses/${id}`, {
      method: 'DELETE',
      body: { confirm: true },
    }),
};

// ---------------- AI (OpenRouter) status & enhancement ----------------

export const ai = {
  status: () => api<AiStatus>('/ai/status'),
  enhance: (analysisId: string) =>
    api<{ executiveSummary: string; polishedRequirements: string[]; redFlags: string[] }>(
      '/ai/enhance',
      { method: 'POST', body: { analysisId } },
    ),
  draftSection: (opportunityId: string, sectionId: string, guidance?: string) =>
    api<AiDraftSectionResult>('/ai/draft-section', {
      method: 'POST',
      body: { opportunityId, sectionId, ...(guidance ? { guidance } : {}) },
    }),
  concierge: (message: string, history: ConciergeHistoryItem[] = [], mode: 'standard' | 'onboard' = 'standard', voice = false, opportunityId?: string | null) =>
    api<ConciergeTurn>('/ai/concierge', {
      method: 'POST',
      body: { message, history, mode, voice, ...(opportunityId ? { opportunityId } : {}) },
    }),
  /** Val's premium voice — MP3 blob. Throws HttpError 503 when not connected. */
  speak: async (text: string): Promise<Blob> => {
    const res = await fetch('/api/v1/ai/speak', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new HttpError('Voice not available', 'voice_not_connected', res.status);
    return await res.blob();
  },
};

// ---------------- Morning brief & activity feed ----------------

export const brief = {
  get: () => api<MorningBrief>('/org/brief'),
};

export const activity = {
  list: (query?: { actorType?: string; page?: number; limit?: number }) =>
    api<ActivityPage>('/org/activity', { query }),
};

// ---------------- Amendment radar ----------------

export const radar = {
  watchStatus: (opportunityId: string) =>
    api<WatchStatus>(`/org/radar/opportunities/${opportunityId}/watch`),
  watch: (opportunityId: string) =>
    api<{ watching: boolean; id: string }>(`/org/radar/opportunities/${opportunityId}/watch`, {
      method: 'POST',
    }),
  unwatch: (opportunityId: string) =>
    api<{ watching: boolean }>(`/org/radar/opportunities/${opportunityId}/watch`, {
      method: 'DELETE',
    }),
  check: (opportunityId: string) =>
    api<RadarCheckResult>(`/org/radar/opportunities/${opportunityId}/check`, { method: 'POST' }),
  alerts: (query?: { unread?: boolean; page?: number; limit?: number }) =>
    api<AlertPage>('/org/radar/alerts', {
      query: query?.unread ? { ...query, unread: '1' } : query,
    }),
  markAlertRead: (alertId: string) =>
    api<OpportunityAlert>(`/org/radar/alerts/${alertId}/read`, { method: 'POST' }),
  markAllAlertsRead: () =>
    api<{ marked: number }>('/org/radar/alerts/read-all', { method: 'POST' }),
};

// ---------------- Post-award tracking ----------------

export interface AwardInput {
  title: string;
  funder?: string;
  amount?: number;
  awardDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  opportunityId?: string;
  proposalId?: string;
  notes?: string | null;
  status?: AwardStatus;
}

export interface DeliverableInput {
  title: string;
  kind?: DeliverableKind;
  dueDate?: string | null;
  notes?: string | null;
  status?: DeliverableStatus;
}

export const awards = {
  list: (query?: { status?: string; page?: number; limit?: number }) =>
    api<AwardPage>('/org/awards', { query }),
  create: (body: AwardInput) => api<AwardSummary>('/org/awards', { method: 'POST', body }),
  // R2-BR-D50: the caller supplies the amount the user actually
  // entered/confirmed (null/omitted = amount unknown). The opportunity's
  // estimated value is never sent as the award amount.
  fromProposal: (proposalId: string, body?: { amount?: number | null }) =>
    api<AwardSummary>(`/org/awards/from-proposal/${proposalId}`, { method: 'POST', body: body ?? {} }),
  get: (id: string) => api<AwardDetail>(`/org/awards/${id}`),
  update: (id: string, body: Partial<AwardInput>) =>
    api<AwardSummary>(`/org/awards/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => api<{ ok: true }>(`/org/awards/${id}`, { method: 'DELETE' }),
  addDeliverable: (id: string, body: DeliverableInput) =>
    api<AwardDeliverable>(`/org/awards/${id}/deliverables`, {
      method: 'POST',
      body,
    }),
  updateDeliverable: (id: string, deliverableId: string, body: Partial<DeliverableInput>) =>
    api<AwardDeliverable>(`/org/awards/${id}/deliverables/${deliverableId}`, {
      method: 'PATCH',
      body,
    }),
  removeDeliverable: (id: string, deliverableId: string) =>
    api<{ ok: true }>(`/org/awards/${id}/deliverables/${deliverableId}`, { method: 'DELETE' }),
};

// ---------------- Readiness checklists (Phase 2) ----------------

export const readiness = {
  overview: () => api<ReadinessOverview>('/org/readiness/overview'),
  updateItem: (itemKey: string, body: { status?: ReadinessStatus; notes?: string | null; attested?: boolean }) =>
    api<ReadinessItem>(`/org/readiness/items/${encodeURIComponent(itemKey)}`, {
      method: 'PATCH',
      body,
    }),
  uploadEvidence: (itemKey: string, formData: FormData) =>
    apiUpload<{
      document: { id: string; title: string; fileName: string; mimeType: string; sizeBytes: number; createdAt: string };
      itemKey: string;
      evidenceUrl: string;
      reviewStatus: string;
    }>(`/org/readiness/items/${encodeURIComponent(itemKey)}/evidence`, formData),
  wizards: () => api<{ wizards: WizardDef[] }>('/org/readiness/wizards'),
  runWizard: (wizardKey: string, answers: Record<string, boolean>) =>
    api<WizardRunResult>(`/org/readiness/wizards/${encodeURIComponent(wizardKey)}/run`, {
      method: 'POST',
      body: { answers },
    }),
};

// ---------------- First-run onboarding wizard ----------------

export const onboarding = {
  get: () => api<{ profile: OnboardingProfile | null }>('/org/onboarding'),
  upsert: (body: OnboardingUpsertBody) =>
    api<{ profile: OnboardingProfile }>('/org/onboarding', {
      method: 'PUT',
      body,
    }),
  personalize: (body: { path: OnboardingPath; ein?: string | null; uei?: string | null; stage: OnboardingStage; taxExemptStatus?: TaxExemptStatus | null; samStatus?: SamStatus | null; certifications?: string[] }) =>
    api<{ plan: PersonalizedPlan }>('/org/onboarding/personalize', {
      method: 'POST',
      body,
    }),
  complete: () =>
    api<{ profile: OnboardingProfile }>('/org/onboarding/complete', { method: 'POST' }),
  restart: () =>
    api<{ profile: OnboardingProfile }>('/org/onboarding/restart', { method: 'POST' }),
};

// ---------------- Document vault (Phase 3) ----------------

export const vault = {
  list: () => api<VaultList>('/org/vault'),
  upload: (formData: FormData) =>
    apiUpload<VaultUploadResult>('/org/vault', formData),
  rereview: (id: string) =>
    api<VaultUploadResult>(`/org/vault/${encodeURIComponent(id)}/re-review`, {
      method: 'POST',
    }),
  download: (id: string) => apiDownload(`/org/vault/${encodeURIComponent(id)}/download`),
  remove: (id: string) =>
    api<{ ok: true }>(`/org/vault/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ---------------- Nonprofit org lookup (onboarding auto-fill) ----------------

export const nonprofits = {
  sources: () =>
    api<{ active: NonprofitDataSource; sources: NonprofitSourceInfo[] }>(
      '/org/nonprofits/sources',
    ),
  search: (q: string, limit?: number) =>
    api<{ total: number; source: NonprofitDataSource; sourceLabel: string; items: NonprofitSearchItem[] }>('/org/nonprofits/search', {
      query: { q, limit },
    }),
  get: (ein: string) =>
    api<{ organization: NonprofitOrganization }>(
      `/org/nonprofits/${encodeURIComponent(ein)}`,
    ),
};

export const registration = {
  get: () => api<RegistrationView>('/org/registration'),
  updateProfile: (body: { uei?: string | null; ein?: string | null; legalBusinessName?: string | null }) =>
    api<{ organization: { name: string | null; uei: string | null; ein: string | null } }>('/org/registration/profile', {
      method: 'PATCH',
      body,
    }),
  refreshSam: (body?: { uei?: string; force?: boolean }) =>
    api<SamRefreshResult>('/org/registration/refresh', { method: 'POST', body: body ?? {} }),
  refreshTax: (body?: { ein?: string }) =>
    api<TaxRefreshResult>('/org/registration/tax-exempt/refresh', { method: 'POST', body: body ?? {} }),
};

